import React, { useEffect, useMemo, useState } from 'react';
import { useCartContext } from './CartContext';
import Button from '@components/atoms/Button';
import Icon from '@components/atoms/Icon';

interface GwpProductProps {
  product: {
    handle: string;
    title: string;
    available: boolean;
    price: string;
    compare_at_price?: string;
    variants: Array<{
      id: number;
      options: string[];
      price: string;
      compareAtPrice?: string;
      available: boolean;
      image?: {
        src: string;
      };
    }>;
  };
  excludedVariants: string[];
}

const GwpProduct: React.FC<GwpProductProps> = ({ product, excludedVariants }) => {
  const { cart } = useCartContext();
  const [selectedVariant, setSelectedVariant] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Filter out excluded variants
  const filteredVariants = useMemo(() => {
    return product.variants.filter(
      (variant) => !excludedVariants.includes(String(variant.id))
    );
  }, [product.variants, excludedVariants]);

  // Set initial variant if it's not excluded
  useEffect(() => {
    if (filteredVariants.length > 0) {
      setSelectedVariant(Number(filteredVariants[0].id));
    }
  }, [filteredVariants]);

  // Handle variant selection
  const handleVariantChange = (variantId: number) => {
    setSelectedVariant(variantId);
  };

  // Add selected GWP to the cart
  const addGwpToCart = async (id: number) => {
    setIsAdding(true); // Disable the button to prevent multiple clicks
    try {
      await cart.add(
        [
          {
            id,
            quantity: 1,
            attributes: [{ key: 'type', value: 'gwp' }],
          },
        ],
        false
      );
    } finally {
      setIsAdding(false); // Re-enable the button after the operation is complete
    }
  };

  return (
    <div className="flex items-center gap-4 justify-between">
      <div className="flex items-center gap-4">
        <img
          src={product.variants[0]?.image?.src || ''}
          alt={product.title}
          className="w-16 h-16 object-cover rounded"
        />
        <div>
          <p className="body-xxs text-black font-bold">{product.title}</p>
          <div className="flex items-center gap-2">
            {product.compare_at_price ? (
              <p className="body-sm util line-through decoration-1 text-black">
                {product.compare_at_price}
              </p>
            ) : (
              <p className="body-sm util line-through decoration-1 text-black">{product.price}</p>
            )}
            <span className="body-sm text-black font-bold">{window.DEPT.Strings.cart.freeGift}</span>
          </div>
          {filteredVariants.length > 1 && (
            <div className="body-xxs mt-2 border-b border-b-grey-8 max-w-max flex items-center relative">
              <label htmlFor={`variant-${product.handle}`} className="flex items-center gap-1 cursor-pointer">
                <select
                  id={`variant-${product.handle}`}
                  value={selectedVariant || ''}
                  onChange={(e) => handleVariantChange(Number(e.target.value))}
                  className="select-no-arrow p-0 pr-5 cursor-pointer body-xxs"
                >
                  {filteredVariants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.options.join(' / ') + (!variant.available ? ' - Sold Out' : '')}
                    </option>
                  ))}
                </select>
                <Icon name="chevron-down" className="absolute right-0 text-black w-4 h-4 pointer-events-none" />
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        {product.available ? (
          <Button
            mode="button"
            buttonText={isAdding ? "Adding..." : "Add to cart"}
            onClick={() => addGwpToCart(selectedVariant!)}
            disabled={isAdding} // Disable the button while the item is being added to prevent multiple clicks
            className="min-w-22.5 min-h-7.5 max-h-7.5 button--sm"
          />
        ) : (
          <span className="text-red-500 text-xs">{window.DEPT.Strings.cart.outOfStock}</span>
        )}
      </div>
    </div>
  );
};

export default GwpProduct;
